import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import { FileCopy } from 'Components/Atoms/Icons';
import Typography from 'Components/Atoms/Typography';
import { Severity } from 'Contexts/RestaurantContext';
import useAlert from 'CustomHooks/useAlert';
import useRestaurant from 'CustomHooks/useRestaurant';
import useToast from 'CustomHooks/useToast';
import { Reservation } from 'types/reservations';
import {
  Claim,
  IN_FLIGHT_ATTEMPT_STATUSES,
  SmsDelivery,
} from 'types/noShowProtection';
import {
  abandonAttempt,
  cancelClaim,
  createPaymentLink,
  reconcileClaim,
  requestProtection,
  resendProtectionSms,
  retryCharge,
  waiveProtection,
} from 'App/NoShowProtection/api';
import {
  apiErrorMessage,
  CLAIM_STATUS_LABEL,
  formatCents,
  formatDateTime,
  LIABILITY_KIND_LABEL,
  newRequestId,
  PROTECTION_STATUS_LABEL,
} from 'App/NoShowProtection/domain';
import ClaimDialog from './ClaimDialog';
import useReservationProtection from './useReservationProtection';
import RefundDialog, { refundableCents } from './RefundDialog';
import './styles.scss';

const NS = 'noShowProtection';

const ATTEMPT_STATUS_LABEL: Record<string, string> = {
  creating: 'Processing',
  open: 'Open',
  pending: 'Processing',
  authorized: 'Processing',
  paid: 'Paid',
  failed: 'Failed',
  canceled: 'Cancelled',
  expired: 'Expired',
  unknown: 'Unclear',
  abandoned: 'Given up',
};

/** Unclear attempts without a Mollie payment, which the team may give up after a check. */
const isUnclearAttempt = (attempt: Claim['attempts'][number]) =>
  ['creating', 'unknown'].includes(attempt.status) && !attempt.paymentId;

const hasAttemptInFlight = (claim: Claim) =>
  claim.attempts.some((a) => IN_FLIGHT_ATTEMPT_STATUSES.includes(a.status));

const openPaymentLink = (claim: Claim) =>
  claim.attempts
    .filter((a) => a.type === 'link' && a.status === 'open' && a.checkoutUrl)
    .map((a) => a.checkoutUrl as string)
    .pop();

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box className="no-show-protection__row">
    <Typography variant="text-3" translation={null}>
      {label}
    </Typography>
    <Typography variant="text-3" color="subdued" translation={null}>
      {value}
    </Typography>
  </Box>
);

const NoShowProtectionSection: React.FC<{ reservation: Reservation }> = ({
  reservation,
}) => {
  const { t } = useTranslation(NS);
  const { restaurantId, apps } = useRestaurant();
  const alert = useAlert();
  const toast = useToast();

  const isMollieProtected = reservation.noShowProtection?.provider === 'mollie';
  const enabled = isMollieProtected || apps.includes('noShowMollie');

  const { state, reload } = useReservationProtection(
    restaurantId,
    reservation,
    enabled
  );

  const [busy, setBusy] = useState(false);
  const resendRequestId = useRef<string | null>(null);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [refundClaimId, setRefundClaimId] = useState<string | null>(null);
  const [linkUrls, setLinkUrls] = useState<Record<string, string>>({});

  const protection = state?.protection ?? null;
  const claims = state?.claims ?? [];
  const canCharge = !!state?.canCharge;

  const canStillRequest =
    !!state?.matchingTemplate &&
    String(reservation.status) !== 'failed' &&
    !reservation.done;

  if (!enabled || !restaurantId || (!protection && !canStillRequest)) {
    return null;
  }

  const run = async (action: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    try {
      await action();
      if (success) toast(success, Severity.SUCCESS);
    } catch (e) {
      toast(apiErrorMessage(e, t('Action failed.')), Severity.ERROR);
    } finally {
      setBusy(false);
      reload();
    }
  };

  const askWaive = () =>
    alert({
      title: t('Release without protection?'),
      titleTranslation: null,
      description: t(
        'The reservation stays confirmed without a deposited payment method. This exception is documented.'
      ),
      descriptionTranslation: null,
      inputFieldLabel: t('Note (optional)'),
      inputFieldLabelTranslation: null,
      submitLabel: t('Release'),
      onSubmit: (_secondary, note) =>
        run(
          () =>
            waiveProtection(restaurantId, reservation.id, note || undefined),
          t('Released without protection.')
        ),
    });

  const askCancelClaim = (claim: Claim) =>
    alert({
      title: t('Cancel claim?'),
      titleTranslation: null,
      description: t(
        'The open claim of {{amount}} is cancelled; the amount can be claimed again later.',
        {
          amount: formatCents(claim.amountCents),
        }
      ),
      descriptionTranslation: null,
      submitLabel: t('Cancel claim'),
      onSubmit: () => run(() => cancelClaim(restaurantId, claim.id)),
    });

  const askAbandonAttempt = (claim: Claim, attemptId: string) =>
    alert({
      title: t('Give up the unclear attempt?'),
      titleTranslation: null,
      description: t(
        'Mollie is checked again completely. Only an attempt older than one hour without any Mollie payment is closed; afterwards the fee can be charged again.'
      ),
      descriptionTranslation: null,
      submitLabel: t('Give up attempt'),
      onSubmit: () =>
        run(async () => {
          const { outcome } = await abandonAttempt(
            restaurantId,
            claim.id,
            attemptId
          );
          toast(
            outcome === 'abandoned'
              ? t('Attempt given up. The fee can be charged again.')
              : t(
                  'Mollie confirmed a payment for this attempt; its status was updated.'
                ),
            Severity.SUCCESS
          );
        }),
    });

  const requestLink = (claim: Claim) =>
    run(async () => {
      const { checkoutUrl } = await createPaymentLink(
        restaurantId,
        claim.id,
        newRequestId()
      );
      if (checkoutUrl) {
        setLinkUrls((urls) => ({ ...urls, [claim.id]: checkoutUrl }));
      }
    }, t('Payment link created and sent to the guest.'));

  const copy = (url: string) => {
    navigator.clipboard.writeText(url);
    toast(t('Link copied'), Severity.SUCCESS);
  };

  // One id per click: a double click or a retried request sends one SMS.
  const resendSms = () => {
    resendRequestId.current = resendRequestId.current ?? newRequestId();
    const requestId = resendRequestId.current;
    return run(
      () =>
        resendProtectionSms(restaurantId, reservation.id, requestId).finally(
          () => {
            resendRequestId.current = null;
          }
        ),
      t('SMS queued again. The deadline is not extended.')
    );
  };

  const smsDeliveryText = (delivery: SmsDelivery) => {
    const at = formatDateTime(delivery.at ?? delivery.queuedAt);
    switch (delivery.status) {
      case 'accepted':
        return `${t('Accepted by the SMS provider')} · ${at}`;
      case 'failed':
        return delivery.reason
          ? t('SMS failed: {{reason}}', { reason: delivery.reason })
          : t('SMS failed');
      case 'queued':
        return `${t('SMS queued')} · ${at}`;
      default:
        return t('SMS status unclear – check the number or resend');
    }
  };

  const noPermissionHint = !canCharge && (
    <Typography variant="text-4" color="subdued" translation={null} block>
      {t('You are not allowed to charge or refund fees. Ask an admin.')}
    </Typography>
  );

  const statusLine = () => {
    if (!protection) {
      return t('Template {{name}} applies – not protected yet', {
        name: state?.matchingTemplate?.name ?? '',
      });
    }
    return t(PROTECTION_STATUS_LABEL[protection.status]);
  };

  const openReviews =
    protection?.observations.filter((o) => !o.reviewed).length ?? 0;

  const refundTarget = claims.find((c) => c.id === refundClaimId);

  return (
    <Box className="no-show-protection">
      <div className="no-show-protection__heading">
        <Typography variant="text-3" weight="bold" translation={null}>
          {t('No-Show Protection')}
        </Typography>
        {protection && (
          <span
            className={`no-show-protection__status no-show-protection__status--${protection.status}`}
          >
            {statusLine()}
          </span>
        )}
      </div>
      {!protection && (
        <p className="no-show-protection__meta">{statusLine()}</p>
      )}
      {protection && (
        <p className="no-show-protection__meta">
          {t('{{n}} persons', { n: protection.terms.guests })} ·{' '}
          {protection.terms.templateName}
        </p>
      )}

      {protection?.status === 'pending' && (
        <>
          <Row
            label={t('Deadline')}
            value={
              protection.deadline
                ? formatDateTime(protection.deadline)
                : t('SMS not sent yet')
            }
          />
          {state?.smsDelivery ? (
            <Row label={t('Last SMS')} value={smsDeliveryText(state.smsDelivery)} />
          ) : (
            !!protection.smsSentAt && (
              <Row
                label={t('SMS queued')}
                value={formatDateTime(protection.smsSentAt)}
              />
            )
          )}
        </>
      )}

      {protection?.needsReconsent && (
        <Typography
          variant="text-3"
          color="error"
          translation={null}
          block
          style={{ margin: '4px 0' }}
        >
          {t('New consent required – protected so far: {{n}} persons', {
            n: protection.terms.guests,
          })}
        </Typography>
      )}

      {protection?.status === 'waived' && protection.waived && (
        <Row
          label={t('Released')}
          value={`${formatDateTime(protection.waived.ts)}${
            protection.waived.note ? ` – ${protection.waived.note}` : ''
          }`}
        />
      )}

      {protection && (
        <>
          {protection.totals.requestedCents > 0 && (
            <>
              <Row
                label={t('Claimed')}
                value={formatCents(protection.totals.requestedCents)}
              />
              <Row
                label={t('Collected')}
                value={formatCents(protection.totals.collectedCents)}
              />
            </>
          )}
          {!!protection.totals.refundedCents && (
            <Row
              label={t('Refunded')}
              value={formatCents(protection.totals.refundedCents)}
            />
          )}
          {!!openReviews && (
            <Typography
              variant="text-3"
              color="secondary"
              translation={null}
              block
              style={{ margin: '4px 0' }}
            >
              {t('{{n}} cancellation(s) or reduction(s) to review', {
                n: openReviews,
              })}
            </Typography>
          )}
        </>
      )}

      {claims.length > 0 && (
        <details className="no-show-protection__history">
          <summary>
            {t('Fees and history')} ({claims.length})
          </summary>
          {claims.map((claim) => {
            const link = linkUrls[claim.id] ?? openPaymentLink(claim);
            const inFlight = hasAttemptInFlight(claim);
            return (
              <Box key={claim.id} className="no-show-protection__claim">
                <Box className="no-show-protection__claim-heading">
                  <Typography variant="text-3" weight="bold" translation={null}>
                    {`${t(LIABILITY_KIND_LABEL[claim.kind])} (${
                      claim.persons
                    })`}
                  </Typography>
                  <Typography variant="text-3" translation={null}>
                    {`${formatCents(claim.amountCents)} · ${t(
                      CLAIM_STATUS_LABEL[claim.status]
                    )}`}
                  </Typography>
                </Box>
                <Typography
                  variant="text-4"
                  color="subdued"
                  translation={null}
                  block
                >
                  {formatDateTime(claim.createdAt)}
                  {claim.paidCents
                    ? ` · ${t('paid')} ${formatCents(claim.paidCents)}`
                    : ''}
                  {claim.refundedCents
                    ? ` · ${t('refunded')} ${formatCents(claim.refundedCents)}`
                    : ''}
                </Typography>
                {claim.attempts.map((attempt) => (
                  <Typography
                    key={attempt.id}
                    variant="text-4"
                    color="subdued"
                    translation={null}
                    block
                  >
                    {`${formatDateTime(attempt.createdAt)} – ${
                      attempt.type === 'link' ? t('Payment link') : t('Charge')
                    }: ${t(
                      ATTEMPT_STATUS_LABEL[attempt.status] ?? attempt.status
                    )}${
                      attempt.failureReason ? ` (${attempt.failureReason})` : ''
                    }`}
                    {isUnclearAttempt(attempt) && (
                      <Button
                        variant="transparent"
                        translation={null}
                        disabled={!canCharge || busy}
                        onClick={() => askAbandonAttempt(claim, attempt.id)}
                      >
                        {t('Give up attempt')}
                      </Button>
                    )}
                  </Typography>
                ))}
                {!!claim.duplicatePayments?.length && (
                  <Typography
                    variant="text-4"
                    color="error"
                    translation={null}
                    block
                  >
                    {t(
                      'A second payment was received for this claim. Please refund it in Mollie.'
                    )}
                  </Typography>
                )}
                {claim.paidAfterCancel && (
                  <Typography variant="text-4" color="error" translation={null} block>
                    {t(
                      '{{amount}} were paid after this claim was cancelled. Check and refund if needed.',
                      { amount: formatCents(claim.paidAfterCancel.amountCents) }
                    )}
                  </Typography>
                )}
                {claim.exceedsCap && (
                  <Typography variant="text-4" color="error" translation={null} block>
                    {t(
                      'This occasion collected {{collected}}, more than the agreed {{cap}}. Please refund the difference.',
                      {
                        collected: formatCents(claim.exceedsCap.collectedCents),
                        cap: formatCents(claim.exceedsCap.capCents),
                      }
                    )}
                  </Typography>
                )}
                {link && (
                  <Box flex style={{ alignItems: 'center', gap: 4 }}>
                    <Typography
                      variant="text-4"
                      translation={null}
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {link}
                    </Typography>
                    <IconButton
                      size="small"
                      color="subdued"
                      onClick={() => copy(link)}
                    >
                      <FileCopy fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Box flex style={{ flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {claim.status === 'failed' && !inFlight && (
                    <>
                      <Button
                        variant="transparent"
                        color="primary"
                        translation={null}
                        disabled={!canCharge || busy}
                        onClick={() =>
                          run(() =>
                            retryCharge(restaurantId, claim.id, newRequestId())
                          )
                        }
                      >
                        {t('Retry charge')}
                      </Button>
                      <Button
                        variant="transparent"
                        color="primary"
                        translation={null}
                        disabled={!canCharge || busy}
                        onClick={() => requestLink(claim)}
                      >
                        {t('Payment link')}
                      </Button>
                    </>
                  )}
                  {['open', 'failed'].includes(claim.status) && !inFlight && (
                    <Button
                      variant="transparent"
                      translation={null}
                      disabled={!canCharge || busy}
                      onClick={() => askCancelClaim(claim)}
                    >
                      {t('Cancel claim')}
                    </Button>
                  )}
                  {(claim.status === 'processing' || inFlight) && (
                    <Button
                      variant="transparent"
                      translation={null}
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          const result = await reconcileClaim(
                            restaurantId,
                            claim.id
                          );
                          if (result.incompleteSearches?.length)
                            toast(
                              t(
                                'Mollie could not be searched completely. Nothing was changed; please check again later.'
                              ),
                              Severity.WARNING
                            );
                        })
                      }
                    >
                      {t('Check status')}
                    </Button>
                  )}
                  {refundableCents(claim) > 0 && (
                    <Button
                      variant="transparent"
                      translation={null}
                      disabled={!canCharge || busy}
                      onClick={() => setRefundClaimId(claim.id)}
                    >
                      {t('Refund')}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </details>
      )}

      <Box className="no-show-protection__actions">
        {protection?.status === 'secured' && (
          <Button
            style={{ border: '1px solid var(--color-border)' }}
            translation={null}
            disabled={!canCharge || busy}
            onClick={() => setClaimDialogOpen(true)}
          >
            {t('Claim fee')}
          </Button>
        )}
        {((!protection && canStillRequest) || protection?.needsReconsent) && (
          <Button
            style={{ border: '1px solid var(--color-border)' }}
            translation={null}
            disabled={busy}
            onClick={() =>
              run(
                () => requestProtection(restaurantId, reservation.id, true),
                t('SMS queued.')
              )
            }
          >
            {protection
              ? t('Send SMS for new consent')
              : t('Request deposit via SMS')}
          </Button>
        )}
        {protection?.status === 'pending' && (
          <>
            <Button
              style={{ border: '1px solid var(--color-border)' }}
              translation={null}
              disabled={busy}
              onClick={resendSms}
            >
              {t('Resend SMS')}
            </Button>
            <Button
              style={{ border: '1px solid var(--color-border)' }}
              translation={null}
              disabled={!canCharge || busy}
              onClick={askWaive}
            >
              {t('Release without protection')}
            </Button>
          </>
        )}
        {noPermissionHint}
      </Box>

      {claimDialogOpen && protection && (
        <ClaimDialog
          allowNoShow={reservation.status !== 'failed' || !!reservation.noShow}
          restaurantId={restaurantId}
          reservationId={reservation.id}
          protection={protection}
          claims={claims}
          currentGuests={reservation.guests}
          onClose={() => setClaimDialogOpen(false)}
          onCreated={reload}
        />
      )}
      {refundTarget && (
        <RefundDialog
          restaurantId={restaurantId}
          claim={refundTarget}
          onClose={() => setRefundClaimId(null)}
          onRefunded={reload}
        />
      )}
    </Box>
  );
};

export default NoShowProtectionSection;
